import { useState, type FormEvent } from 'react';

import { Button, Card, Field, Input, Toggle } from '@/design-system';
import { GRACE_CREDITS_MAX, type CreditRules } from '@/entities/tenant';

import { adminCopy } from '../copy';

export interface CreditRulesCardProps {
    tenantName: string;
    rules: CreditRules;
    onSave: (rules: CreditRules) => void;
    saving?: boolean;
    notice?: string | null;
    error?: string | null;
}

/** Een heel aantal keuringen van 0 tot en met het maximum, of `null`. */
function graceOf(value: string): number | null {
    if (!/^\d+$/.test(value.trim())) {
        return null;
    }

    const parsed = Number(value.trim());

    return parsed <= GRACE_CREDITS_MAX ? parsed : null;
}

/**
 * De tegoedregels van één bedrijf: of nieuwe keuringen stoppen als de keuringen
 * op zijn, en hoe ver het saldo onder nul mag.
 *
 * Alleen het platform zet dit (`PATCH /platform/tenants/{tenant}/credit-rules`);
 * de eigenaar ziet het in Instellingen als uitleg. Beide regels gaan samen met
 * één knop, want het zijn één besluit: zonder blokkeren is er geen marge, en het
 * veld staat er dan ook niet.
 */
export function CreditRulesCard({
    tenantName,
    rules,
    onSave,
    saving = false,
    notice = null,
    error = null,
}: CreditRulesCardProps) {
    const [block, setBlock] = useState(rules.blockOnEmpty);
    const [grace, setGrace] = useState(String(rules.graceCredits));
    const graceValue = graceOf(grace);
    const valid = !block || graceValue !== null;

    function submit(event: FormEvent) {
        event.preventDefault();

        if (!valid) {
            return;
        }

        onSave({ blockOnEmpty: block, graceCredits: block ? (graceValue ?? 0) : 0 });
    }

    return (
        <Card padding="md" as="section" aria-labelledby="credit-rules-title">
            <h2
                id="credit-rules-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {adminCopy.creditRulesTitle}
            </h2>
            <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {adminCopy.creditRulesIntro(tenantName)}
            </p>

            <form onSubmit={submit} className="mt-3 grid gap-[10px]">
                <Toggle
                    label={adminCopy.creditRulesBlock}
                    description={
                        block ? adminCopy.creditRulesBlockOn : adminCopy.creditRulesBlockOff
                    }
                    checked={block}
                    disabled={saving}
                    onCheckedChange={setBlock}
                />

                {block ? (
                    <Field
                        label={adminCopy.creditRulesGrace}
                        size="sm"
                        description={adminCopy.creditRulesGraceHint(GRACE_CREDITS_MAX)}
                        error={
                            graceValue === null
                                ? adminCopy.creditRulesGraceInvalid(GRACE_CREDITS_MAX)
                                : undefined
                        }
                    >
                        <Input
                            mono
                            inputMode="numeric"
                            value={grace}
                            onChange={(event) => setGrace(event.target.value)}
                        />
                    </Field>
                ) : null}

                <div>
                    <Button type="submit" variant="outline" size="sm" disabled={saving || !valid}>
                        {adminCopy.creditRulesSave}
                    </Button>
                </div>
            </form>

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-3 text-[12px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {error ? (
                <p role="alert" className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {error}
                </p>
            ) : null}
        </Card>
    );
}
